import { Cursor } from '@studiometa/ui';
import matrix from '@studiometa/js-toolkit/utils/css/matrix';
import anime from 'animejs';

/**
 * @typedef {Object} TeamRef
 * @property {HTMLElement} cursor
 * @property {HTMLElement[]} member[]
 * @property {HTMLElement} panel
 * @property {HTMLElement} body
 * @property {HTMLElement} article
 * @property {HTMLElement} close
 */

/**
 * @typedef {Object} TeamInterface
 * @property {TeamRef} $refs
 * @property {Number} index
 * @property {Boolean} animating
 */

/**
 * Homepage class.
 */
export default class Team extends Cursor {
  /**
   * Class config
   * @type {Object}
   */
  static config = {
    ...Cursor.config,
    name: 'Team',
    refs: ['cursor', 'member[]', 'panel', 'body', 'article', 'close'],
    options: {
      ...Cursor.config.options,
      translateDampFactor: {
        type: Number,
        default: 0.1,
      },
    },
  };

  /**
   * Initialize the component's behavior.
   *
   * @this {Team & TeamInterface}
   */
  mounted() {}

  /**
   * Render the cursor.
   *
   * @param  {Object} options
   * @param  {Number} options.x
   * @param  {Number} options.y
   * @param  {Number} options.scale
   * @this   {Team & TeamInterface}
   * @return {void}
   */
  render({ x, y }) {
    const transform = matrix({
      translateX: x,
      translateY: y,
    });

    this.$refs.cursor.style.transform = `translateZ(0) ${transform}`;
  }

  /**
   * Show cursor.
   *
   * @this {Team & TeamInterface}
   */
  onMemberMousemove() {
    if (!this.$refs.cursor.classList.contains('active')) {
      this.$refs.cursor.classList.add('active');
    }
  }

  /**
   * Hide cursor.
   *
   * @this {Team & TeamInterface}
   */
  onMemberMouseleave() {
    if (this.$refs.cursor.classList.contains('active')) {
      this.$refs.cursor.classList.remove('active');
    }
  }

  /**
   * Toggle panel.
   *
   * @this {Team & TeamInterface}
   * @param {TouchEvent | MouseEvent} event
   * @param {Number} index
   */
  onMemberClick(event, index) {
    if (this.animating) return;
    this.openPanel(index);
  }

  /**
   * Open panel.
   *
   * @this {Team & TeamInterface}
   * @param {Number} index
   */
  openPanel(index) {
    this.$refs.body.innerHTML = this.$refs.member[index].querySelector('.member-details').innerHTML;

    requestAnimationFrame(() => {
      this.animating = true;
      this.$refs.panel.classList.add('active');
      document.body.style.overflow = 'hidden';

      anime({ targets: this.$refs.panel, opacity: 1, duration: 400, easing: 'linear' });

      anime({
        targets: this.$refs.article,
        translateX: [0, -40],
        duration: 900,
        easing: 'easeInOutCubic',
      });

      anime({
        targets: this.$refs.close,
        opacity: 1,
        scale: [0.6, 1],
        delay: 700,
        duration: 400,
        easing: 'easeOutBack',
      });

      anime({
        targets: this.$refs.body,
        translateX: ['100%', '0%'],
        duration: 600,
        delay: 300,
        easing: 'easeOutCubic',
        complete: () => {
          this.animating = false;

          /** @type HTMLElement */
          const video = this.$refs.body.querySelector('.embed-container');
          if (!video) return;

          video.innerHTML = video.dataset.innerHtml;
        },
      });
    });
  }

  /**
   * Close panel.
   *
   * @this {Team & TeamInterface}
   */
  closePanel() {
    this.animating = true;
    this.$refs.panel.classList.remove('active');
    document.body.style.overflow = '';

    anime({
      targets: this.$refs.close,
      opacity: 0,
      scale: [1, 0.8],
      duration: 400,
      easing: 'easeInOutCubic',
    });

    anime({
      targets: this.$refs.body,
      translateX: ['0%', '100%'],
      duration: 800,
      easing: 'easeInOutCubic',
    });

    anime({
      targets: this.$refs.article,
      translateX: [0],
      duration: 900,
      easing: 'easeInOutCubic',
    });

    anime({
      targets: this.$refs.panel,
      opacity: 0,
      duration: 600,
      easing: 'linear',
      delay: 400,
      complete: () => {
        this.animating = false;
      },
    });
  }

  /**
   * Close panel on panel click.
   *
   * @this {Team & TeamInterface}
   */
  onPanelClick() {
    if (this.animating) return;
    this.closePanel();
  }

  /**
   * Close panel on close btn click.
   *
   * @this {Team & TeamInterface}
   */
  onCloseClick() {
    if (this.animating) return;
    this.closePanel();
  }

  /**
   * Prevent panel closing.
   *
   * @this {Team & TeamInterface}
   * @param {TouchEvent | MouseEvent} event
   */
  onBodyClick(event) {
    event.stopPropagation();
  }
}
